import { InfiniteListBase, ShowBase, useShowContext } from "ra-core";
import type { ShowBaseProps } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

import { NoteCreate, NotesIterator } from "../notes";
import { tierColor, tierLetter } from "../dealsmap/leadUtils";
import type { Prospect } from "../types";
import { ProspectActivities } from "./ProspectActivities";
import { ProspectAside } from "./ProspectAside";
import { ProspectComps } from "./ProspectComps";
import { primaryPhone } from "./prospectModel";

export const ProspectShow = (props: ShowBaseProps = {}) => (
  <ShowBase {...props}>
    <ProspectShowContent />
  </ShowBase>
);

const ProspectShowContent = () => {
  const { record, isPending } = useShowContext<Prospect>();
  if (isPending || !record) return null;

  const phone = primaryPhone(record);
  const location = [
    record.property_city,
    record.property_state,
    record.property_zip,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="mt-2 mb-2 flex flex-col lg:flex-row gap-8">
      <div className="flex-1">
        <Card>
          <CardContent>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h5 className="text-xl font-semibold">{record.owner_name}</h5>
                <div className="text-sm text-muted-foreground">
                  {record.property_address}
                  {location ? ` · ${location}` : ""}
                </div>
                {phone ? (
                  <div className="text-sm text-muted-foreground">{phone}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-1.5">
                {tierLetter(record.deal_tier) ? (
                  <Badge
                    className="text-white border-transparent"
                    title={record.deal_tier ?? undefined}
                    style={{ backgroundColor: tierColor(record.deal_tier) }}
                  >
                    Tier {tierLetter(record.deal_tier)}
                  </Badge>
                ) : null}
                {record.stage ? (
                  <Badge variant="secondary">{record.stage}</Badge>
                ) : null}
              </div>
            </div>
            <div className="mt-4 border-t pt-4">
              <ProspectActivities prospectId={Number(record.id)} />
            </div>
            <InfiniteListBase
              resource="prospect_notes"
              filter={{ prospect_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={<NoteCreate reference="prospects" className="mt-4" />}
            >
              <NotesIterator reference="prospects" />
            </InfiniteListBase>
          </CardContent>
        </Card>
        <div className="mt-4">
          <ProspectComps />
        </div>
      </div>
      <ProspectAside />
    </div>
  );
};
